/**
 * Where Back goes, per pane (RFC 0302 S7).
 *
 * A stack rather than a flag, because the answer depends on how you arrived. A
 * row picked from the list goes back to the list. A subthread opened from a
 * card inside a conversation goes back to THAT conversation. Before this, both
 * went to the list, which meant opening a branch lost the thread you opened it
 * from — and RFC 0302 names that as why the last orchestrator attempt did not
 * stick: the agent could fan work out and the person could not follow it.
 *
 * PANE-LOCAL, not in the URL and not on the space. It is where this pane has
 * been; a second pane on the same session has its own history and its own Back,
 * which is what makes a split a second way of looking rather than a shared one.
 *
 * SEPARATE FROM THE PANE so it can be tested without a repo, a World, or a
 * mounted session list behind it.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

export interface PaneStop {
  sessionId: string
  projectId: string
}

export interface SessionBack {
  /** Where Back would go, or null when it goes to the session list. */
  target: PaneStop | null
  /** Open a session a card points at, remembering where we were. */
  openFromCard: (sessionId: string) => void
  /** Go back: to the previous session, or to the list when there is none. */
  goBack: () => void
}

export function useSessionBack(
  selectedSessionId: string | null,
  onSelect: (sessionId: string | null, projectId?: string) => void,
  projectOf: (sessionId: string) => string,
): SessionBack {
  const [stack, setStack] = useState<PaneStop[]>([])
  /**
   * The open this pane is expecting from its own card or its own Back.
   * Anything else — a row from the list, a link, the shell re-pointing the pane
   * — is a fresh start and clears the history, because a Back into a
   * conversation you never came from is worse than no Back at all.
   */
  const expected = useRef<string | null>(null)

  useEffect(() => {
    if (selectedSessionId !== null && expected.current === selectedSessionId) {
      expected.current = null
      return
    }
    expected.current = null
    setStack([])
  }, [selectedSessionId])

  const openFromCard = useCallback(
    (sessionId: string) => {
      const from = selectedSessionId
      if (from) {
        expected.current = sessionId
        setStack((s) => [...s, { sessionId: from, projectId: projectOf(from) }])
      }
      onSelect(sessionId, projectOf(sessionId))
    },
    [onSelect, projectOf, selectedSessionId],
  )

  const goBack = useCallback(() => {
    const previous = stack[stack.length - 1]
    if (!previous) {
      onSelect(null)
      return
    }
    expected.current = previous.sessionId
    setStack((s) => s.slice(0, -1))
    onSelect(previous.sessionId, previous.projectId)
  }, [stack, onSelect])

  return { target: stack[stack.length - 1] ?? null, openFromCard, goBack }
}
